import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { map, catchError, switchMap, takeWhile, tap } from 'rxjs/operators';
import { E621Service } from './e621.service';
import { E621Post, ApiSettings } from '../models/e621.models';

export interface FavoritePost {
  id: number;
  url: string;
  previewUrl: string;
  tags: string[];
  rating: string;
  score: number;
  createdAt: string;
  isVideo: boolean;
  fileExt?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  private readonly API_BASE_URL = 'https://e621.net';
  private readonly USER_AGENT = 'DrawfiendShow/1.0 (Web Media Player)';
  private readonly POLL_INTERVAL = 15 * 10000; // 150 seconds

  private http = inject(HttpClient);
  private e621Service = inject(E621Service);

  private favorites$ = new BehaviorSubject<FavoritePost[]>([]);
  private isEnabled$ = new BehaviorSubject<boolean>(false);
  private isLoading$ = new BehaviorSubject<boolean>(false);
  private lastUpdated$ = new BehaviorSubject<Date | null>(null);
  private pollingActive = false;

  constructor() {
    // Subscribe to API settings changes to enable/disable favorites
    this.e621Service.getApiSettings().subscribe(settings => {
      this.updateEnabledState(settings);
    });
  }

  /**
   * Get favorites observable
   */
  getFavorites(): Observable<FavoritePost[]> {
    return this.favorites$.asObservable();
  }

  /**
   * Get enabled state observable
   */
  getIsEnabled(): Observable<boolean> {
    return this.isEnabled$.asObservable();
  }

  /**
   * Get loading state observable
   */
  getIsLoading(): Observable<boolean> {
    return this.isLoading$.asObservable();
  }

  /**
   * Get last updated timestamp observable
   */
  getLastUpdated(): Observable<Date | null> {
    return this.lastUpdated$.asObservable();
  }

  /**
   * Get current favorites synchronously
   */
  getCurrentFavorites(): FavoritePost[] {
    return this.favorites$.value;
  }

  /**
   * Check if a post is favorited
   */
  isFavorited(postId: number): boolean {
    return this.favorites$.value.some(fav => fav.id === postId);
  }

  /**
   * Get favorite posts count
   */
  getFavoritesCount(): number {
    return this.favorites$.value.length;
  }

  /**
   * Get video favorites only
   */
  getVideoFavorites(): FavoritePost[] {
    return this.favorites$.value.filter(fav => fav.isVideo);
  }

  /**
   * Start favorites polling (called when service is enabled)
   */
  private startPolling(): void {
    if (this.pollingActive) {
      return;
    }

    console.log('Starting favorites polling...');
    this.pollingActive = true;

    // Initial load
    this.loadFavorites().subscribe();

    // Set up periodic polling
    timer(this.POLL_INTERVAL, this.POLL_INTERVAL)
      .pipe(
        takeWhile(() => this.pollingActive && this.isEnabled$.value),
        switchMap(() => this.loadFavorites())
      )
      .subscribe({
        next: () => {
          console.log('Favorites polling update completed');
        },
        error: (error) => {
          console.error('Error in favorites polling:', error);
        }
      });
  }

  /**
   * Stop favorites polling
   */
  private stopPolling(): void {
    console.log('Stopping favorites polling...');
    this.pollingActive = false;
  }

  /**
   * Update enabled state based on API settings
   */
  private updateEnabledState(settings: ApiSettings): void {
    const hasCredentials = !!(settings.username && settings.apiKey);
    const wasEnabled = this.isEnabled$.value;

    this.isEnabled$.next(hasCredentials);

    if (hasCredentials && !wasEnabled) {
      // Just became enabled - start polling
      this.startPolling();
    } else if (!hasCredentials && wasEnabled) {
      // Just became disabled - stop polling and clear data
      this.stopPolling();
      this.favorites$.next([]);
      this.lastUpdated$.next(null);
    }

    console.log('Favorites service enabled:', hasCredentials);
  }
  /**
   * Load user favorites from e621 API
   */
  private loadFavorites(): Observable<FavoritePost[]> {
    const settings = this.e621Service.getCurrentApiSettings();

    if (!settings.username || !settings.apiKey) {
      return of([]);
    }

    this.isLoading$.next(true);

    const params = new HttpParams()
      .set('login', settings.username)
      .set('api_key', settings.apiKey);

    const headers = new HttpHeaders({
      // 'User-Agent': this.USER_AGENT,
    });

    console.log(`Loading favorites for user: ${settings.username}`);    return this.http.get<{ posts: E621Post[] }>(`${this.API_BASE_URL}/favorites.json`, {
      params,
      headers,
    }).pipe(
      map(response => this.transformPosts(response.posts)),
      tap(favorites => {
        this.favorites$.next(favorites);
        this.lastUpdated$.next(new Date());
        this.isLoading$.next(false);
        console.log(`Loaded ${favorites.length} favorites (${favorites.filter(f => f.isVideo).length} videos)`);
      }),
      catchError(error => {
        console.error('Error loading favorites:', error);
        this.isLoading$.next(false);

        if (error.status === 403) {
          console.error('Favorites access denied. Check API credentials.');
        } else if (error.status === 429) {
          console.error('Rate limit exceeded for favorites.');
        }

        return of([]);
      })
    );
  }

  /**
   * Transform e621 posts to favorite posts
   */
  private transformPosts(posts: E621Post[]): FavoritePost[] {
    return posts.map(post => ({
      id: post.id,
      url: post.file?.url || '',
      previewUrl: post.preview?.url || '',
      tags: [
        ...post.tags.general,
        ...post.tags.species,
        ...post.tags.character,
        ...post.tags.copyright,
        ...post.tags.artist,
      ],
      rating: post.rating,
      score: post.score.total,
      createdAt: post.created_at,
      isVideo: post.file ? ['webm', 'mp4'].includes(post.file.ext.toLowerCase()) : false,
      fileExt: post.file?.ext,
    }));
  }  /**
   * Add a post to favorites
   */
  addToFavorites(postId: number): Observable<boolean> {
    const settings = this.e621Service.getCurrentApiSettings();

    if (!settings.username || !settings.apiKey) {
      console.warn('Cannot add to favorites: no API credentials');
      return of(false);
    }

    console.log('Adding post to favorites:', postId);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      // 'User-Agent': this.USER_AGENT,
    });

    const body = new URLSearchParams();
    body.set('post_id', postId.toString());
    body.set('login', settings.username);
    body.set('api_key', settings.apiKey);

    return this.http.post(`${this.API_BASE_URL}/favorites.json`, body.toString(), {
      headers,
    }).pipe(
      map(() => {
        console.log('Successfully added to favorites:', postId);
        // Update local favorites list
        this.loadFavorites().subscribe();
        return true;
      }),
      catchError(error => {
        console.error('Error adding to favorites:', error);
        if (error.status === 403) {
          console.error('Access denied when adding to favorites. Check API credentials.');
        } else if (error.status === 422) {
          console.error('Post may already be favorited or invalid post ID.');
        }
        return of(false);
      })
    );
  }  /**
   * Remove a post from favorites
   */
  removeFromFavorites(postId: number): Observable<boolean> {
    const settings = this.e621Service.getCurrentApiSettings();

    if (!settings.username || !settings.apiKey) {
      console.warn('Cannot remove from favorites: no API credentials');
      return of(false);
    }

    console.log('Removing post from favorites:', postId);

    const headers = new HttpHeaders({
      // 'User-Agent': this.USER_AGENT,
    });

    const params = new HttpParams()
      .set('login', settings.username)
      .set('api_key', settings.apiKey);

    return this.http.delete(`${this.API_BASE_URL}/favorites/${postId}.json`, {
      headers,
      params,
    }).pipe(
      map(() => {
        console.log('Successfully removed from favorites:', postId);
        // Update local favorites list
        this.loadFavorites().subscribe();
        return true;
      }),
      catchError(error => {
        console.error('Error removing from favorites:', error);
        if (error.status === 403) {
          console.error('Access denied when removing from favorites. Check API credentials.');
        } else if (error.status === 404) {
          console.error('Post not found in favorites or invalid post ID.');
        }
        return of(false);
      })
    );
  }

  /**
   * Manually refresh favorites
   */
  refreshFavorites(): Observable<FavoritePost[]> {
    console.log('Manually refreshing favorites...');
    return this.loadFavorites();
  }

  /**
   * Clear favorites data (used when disabling service)
   */
  clearFavorites(): void {
    this.favorites$.next([]);
    this.lastUpdated$.next(null);
    console.log('Favorites data cleared');
  }
}
